import { useEffect, useRef, useState } from 'react';
import novaOpsApi from '@/lib/novaOpsApi';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TableCell, TableRow } from '@/components/ui/table';

type SimSecurity = {
  iccid: string | null;
  puk1: string | null;
  puk2: string | null;
};

type Props = {
  accountUuid?: string | null;
  iCustomer?: number | null;
  mvno?: string | null;
  iccid?: string | null;
};

// The parent keys this component by tenant, account and SIM so a new search
// cannot display a previous customer's credentials or a late response.
export function SimPukDetails({ accountUuid, iCustomer, mvno, iccid }: Props) {
  const [details, setDetails] = useState<SimSecurity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);

  async function reveal() {
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (accountUuid) params.set('account_uuid', accountUuid);
      else if (iCustomer) params.set('i_customer', String(iCustomer));
      if (mvno) params.set('mvno', mvno);
      const response = await novaOpsApi.get<SimSecurity>(
        `/ops/customers/sim-security?${params}`,
        {
          signal: controller.signal,
        },
      );
      if (controller.signal.aborted) return;
      if (iccid && response.data.iccid !== iccid) {
        throw new Error(
          'The linked SIM has changed. Search for the customer again before revealing the PUK.',
        );
      }
      setDetails(response.data);
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(
          err instanceof Error
            ? err.message
            : 'Could not load PUK details. Please try again.',
        );
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  return (
    <>
      <TableRow>
        <TableCell className="font-bold">PUK details</TableCell>
        <TableCell>
          <div className="flex flex-col items-start gap-2">
            {details ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDetails(null)}
              >
                Hide PUK details
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={loading || (!accountUuid && !iCustomer)}
                onClick={reveal}
              >
                {loading ? 'Loading PUK details…' : 'Reveal PUK details'}
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              PUK access is recorded in customer history.
            </span>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </TableCell>
      </TableRow>
      {details && (
        <>
          <TableRow>
            <TableCell className="font-bold">PUK 1</TableCell>
            <TableCell className="font-mono">
              {details.puk1 || 'Not available for this SIM'}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-bold">PUK 2</TableCell>
            <TableCell className="font-mono">
              {details.puk2 || 'Not available for this SIM'}
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );
}
